import { useMemo, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import {
  useAppDispatch,
  useAppSelector,
  LabelType,
  LabelStyle,
  notPlacedLabelsSelector,
  labelsSelector,
  preferencesSelector,
  effectiveLabelStyleSelector,
} from '../../../../store';

import rootElement from '../../../../rootElement';
import eventBus from '../../../../eventBus';
import { EditLabelDetailsSection } from './EditLabelDetailsSection';
import { EditLabelStyleSection } from './EditLabelStyleSection';
import { EditLabelToolbar } from './EditLabelToolbar';
import { LabelGroupManagerModal } from './LabelGroupManagerModal';
import { saveEditedLabel } from './saveEditedLabel';
import { useEditLabelGroups } from './useEditLabelGroups';
import { useEditLabelPreview } from './useEditLabelPreview';
import { useEditLabelStyleState } from './useEditLabelStyleState';
import { useEditLabelUiState } from './useEditLabelUiState';
import { resolveLabelEditorStyleState } from './editLabelStyleState';
import { LabelPreviewType } from '../../Map/Labels/LabelEditing';

export interface EditLabelProps {
  marker: LabelType;
  position: { x: number; y: number; rotation?: number; } | null;
  onPreviewChange?: (preview: LabelPreviewType | null) => void;
  onClose: () => void;
  selectedLabelIds: string[];
}

export interface LabelStyleWithRotation extends LabelStyle {
  rotation?: number;
}

export function EditLabel(props: EditLabelProps) {
  const {
    marker,
    position,
    onPreviewChange,
    onClose,
    selectedLabelIds,
  } = props;
  const dispatch = useAppDispatch();
  const labels = useAppSelector(labelsSelector);
  const notPlacedLabels = useAppSelector(notPlacedLabelsSelector);
  const preferences = useAppSelector(preferencesSelector);
  const getEffectiveStyle = useAppSelector(effectiveLabelStyleSelector);

  // Target label id (non-placed label id or "newLabel")
  const [id, setId] = useState(marker.id);
  const [name, setName] = useState(marker.name || '');
  const [changes, setChanges] = useState<Partial<LabelStyleWithRotation>>({});

  const selectedLabels = useMemo(() => {
    const selected = labels.filter((l) => selectedLabelIds.includes(l.id));
    if (marker.id === 'newLabel') {
      const newLabel = { id: 'newLabel', name, preferences: { style: changes }};
      return [ ...selected, newLabel ];
    }
    return selected;
  }, [labels, selectedLabelIds, marker.id, name, changes]);

  const {
    currentStyleState,
    applyStyleState,
    setFontSize,
    setFontWeight,
    setColor,
    setBackgroundColor,
    setBackgroundOpacity,
    setBorderColor,
    setPadding,
    setZIndex,
    setSkewX,
    setSkewY,
    setPerspectiveX,
    setPerspectiveY,
    setVariant,
    rotation,
    setRotation,
  } = useEditLabelStyleState({ marker, id });
  const {
    isGroupManagerOpen,
    openGroupManager,
    closeGroupManager,
    handleDelete,
  } = useEditLabelUiState({ dispatch, markerId: marker.id, onClose });

  const {
    groupName,
    setGroupName,
    groupColor,
    setGroupColor,
    labelSelectionGroups,
    currentLabelGroup,
    canSaveSelectionGroup,
    handleSaveSelectionGroup,
    handleSaveGroupManager,
  } = useEditLabelGroups({
    dispatch,
    labels,
    preferenceGroups: preferences.labelSelectionGroups,
    selectedLabelIds,
  });

  useEditLabelPreview({
    marker,
    newMarkerId: id,
    editedName: name,
    onPreviewChange,
    changes,
  });

  const saveAndClose = useCallback((applyToAll=false, forceChanges:LabelStyleWithRotation|null=null) => {
    const targetLabel = labels.find((l) => l.id === id) ??
      { id: 'newLabel', name, preferences: { style: changes } };
    saveEditedLabel({
      dispatch,
      marker,
      targetLabel,
      name,
      defaultLabelStyle: preferences.defaultLabelStyle ?? {},
      defaultRotation: preferences.defaultLabelRotation ?? 0,
      position,
      changes: forceChanges ?? changes,
      selectedLabels: applyToAll ? labels : selectedLabels,
    });
    onClose();
  },
    [
      dispatch,
      marker,
      id,
      name,
      preferences.defaultLabelStyle,
      preferences.defaultLabelRotation,
      position,
      onClose,
      changes,
      selectedLabels,
      labels,
    ]
  );

  // Update local state when clicking another marker
  useEffect(() => {
    setId(marker.id);
    setName(marker.name ?? '');
    setChanges({});
    setRotation(position?.rotation ?? preferences.defaultLabelRotation ?? 0);
  }, [marker, position?.rotation, preferences.defaultLabelRotation, setRotation]);

  // Update name and reset style changes when changing selected label in dropdown
  useEffect(() => {
    const selectedLabel = labels.find((l) => l.id === id);
    setName(selectedLabel?.name ?? '');
    setChanges({});
    setRotation(position?.rotation ?? preferences.defaultLabelRotation ?? 0);
  }, [id, labels, position?.rotation, preferences.defaultLabelRotation, setRotation]);

  // Save label when clicking Save with edited label opened
  useEffect(() => {
    const flushPendingEdits = (flushState?: { didFlush?: boolean }) => {
      saveAndClose();
      if (flushState) flushState.didFlush = true;
    };

    eventBus.on('flush-pending-edits', flushPendingEdits);
    return () => {
      eventBus.off('flush-pending-edits', flushPendingEdits);
    };
  }, [saveAndClose]);

  const sidebarFooter = rootElement.querySelector('#edit-label-footer');
  if (!sidebarFooter) return null;

  return (
    <div className="label-editor-form">
      <EditLabelToolbar onDelete={handleDelete} />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveAndClose();
        }}
        className="label-editor-form__content"
      >
        {selectedLabels.length === 1 && (
          <EditLabelDetailsSection
            marker={marker}
            notPlacedLabels={notPlacedLabels}
            selectedId={id}
            onSelectedIdChange={setId}
            name={name}
            onNameChange={setName}
          />
        )}

        <EditLabelStyleSection
          marker={marker}
          currentLabelGroup={currentLabelGroup}
          groupName={groupName}
          groupColor={groupColor}
          canSaveSelectionGroup={canSaveSelectionGroup}
          styleState={currentStyleState}
          rotation={rotation ?? preferences.defaultLabelRotation ?? 0}
          onGroupNameChange={setGroupName}
          onGroupColorChange={setGroupColor}
          onManageGroups={openGroupManager}
          onSaveGroup={handleSaveSelectionGroup}
          onAttributeChange={(name, value) => {
            setChanges((prev) => ({ ...prev, [name]: value }));
            switch (name) {
              case 'variant': {
                const nextVariant = String(value) as NonNullable<LabelStyle['variant']>;
                setVariant(nextVariant);
                // Neon glows with the background colour (cyan when unset): show the one in use
                if (changes.backgroundColor === undefined) {
                  setBackgroundColor(resolveLabelEditorStyleState({
                    ...getEffectiveStyle(marker),
                    variant: nextVariant,
                  }).backgroundColor);
                }
                break;
              }
              case 'fontSize':
                setFontSize(Number(value));
                break;
              case 'fontWeight':
                setFontWeight(value === 'bold' ? 'bold' : 'normal');
                break;
              case 'color':
                setColor(String(value));
                break;
              case 'backgroundColor':
                setBackgroundColor(String(value));
                break;
              case 'backgroundOpacity':
                setBackgroundOpacity(Number(value));
                break;
              case 'borderColor':
                setBorderColor(String(value));
                break;
              case 'padding':
                setPadding(Number(value));
                break;
              case 'zIndex':
                setZIndex(Number(value));
                break;
              case 'skewX':
                setSkewX(Number(value));
                break;
              case 'skewY':
                setSkewY(Number(value));
                break;
              case 'perspectiveX':
                setPerspectiveX(Number(value));
                break;
              case 'perspectiveY':
                setPerspectiveY(Number(value));
                break;
              case 'rotation':
                setRotation(Number(value));
                break;
            }
          }}
        />
      </form>
      <LabelGroupManagerModal
        isOpen={isGroupManagerOpen}
        labels={labels}
        groups={labelSelectionGroups}
        onSave={handleSaveGroupManager}
        onClose={closeGroupManager}
      />
      {createPortal(
        <>
          <button
            type="button"
            onClick={() => {
              const defaultStyle = {
                ...preferences.defaultLabelStyle || {},
                rotation: preferences.defaultLabelRotation ?? 0,
              };
              setChanges(defaultStyle);
              setRotation(defaultStyle.rotation);
              applyStyleState(resolveLabelEditorStyleState(defaultStyle));
            }}
            className="label-editor-drawer__button reset"
            title="Reset selected labels to default style"
          >
            Reset to default
          </button>
          <button
            type="button"
            onClick={() => saveAndClose()}
            className="label-editor-drawer__button save"
            disabled={Object.keys(changes).length === 0 && name === marker.name}
            title="Save changes to selected labels"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => {
              const style = {
                ...preferences.defaultLabelStyle || {},
                ...marker.preferences?.style || {},
                ...changes,
                ...{ rotation },
              };
              saveAndClose(true, style);
            }}
            className="label-editor-drawer__button reset"
            title="Copy this label's style to all labels"
          >
            Copy to all
          </button>
          <button
            type="button"
            onClick={() => saveAndClose(true)}
            className="label-editor-drawer__button save-all"
            disabled={Object.keys(changes).length === 0 && name === marker.name}
            title="Apply changes to all labels"
          >
            Apply to all
          </button>
        </>,
        sidebarFooter,
      )}
    </div>
  );
}
